import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { wheelApiService } from '../../services/wheelApi';
import { Promotion } from '../../services/promotionApi';
import { Service } from '../../services/serviceApi';
import { useAuth } from '../../components/AuthProvider';
import { useIrisAvatar } from '../../hooks/useIrisAvatar';
import { 
  MobilePromoWheelConfig, 
  WheelSegment, 
  WheelConfig, 
  WheelData 
} from '../config/MobilePromoWheelConfig';

const MobilePromoWheel: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { avatarUrl } = useIrisAvatar();
  
  // États principaux (identiques au desktop)
  const [segments, setSegments] = useState<WheelSegment[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [wonPromotion, setWonPromotion] = useState<Promotion | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [canPlay, setCanPlay] = useState(true);
  const [services, setServices] = useState<Service[]>([]);
  const [userActivePromotions, setUserActivePromotions] = useState<Promotion[]>([]);
  const [canWin, setCanWin] = useState(true);
  const [daysUntilNextPlay, setDaysUntilNextPlay] = useState(0);
  const [daysUntilNextWin, setDaysUntilNextWin] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [replayDelay, setReplayDelay] = useState(7);
  const [winDelay, setWinDelay] = useState(1);
  const [showRules, setShowRules] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  const wheelRef = useRef<HTMLDivElement>(null);

  // Chargement des données au montage (identique au desktop)
  useEffect(() => {
    loadWheelData();
  }, [isAuthenticated]);

  // Fonction de chargement des données (utilise MobilePromoWheelConfig)
  const loadWheelData = async () => {
    try {
      const { data, services, userActivePromotions } = await MobilePromoWheelConfig.loadWheelData(isAuthenticated);
      
      setServices(services);
      setUserActivePromotions(userActivePromotions);
      
      if (!data.active) {
        setIsActive(false);
        return;
      }
      
      setIsActive(true);
      setCanPlay(data.config.can_play);
      setCanWin(data.config.can_win);
      setDaysUntilNextPlay(data.config.days_until_next_play);
      setDaysUntilNextWin(data.config.days_until_next_win);
      setReplayDelay(data.config.replay_delay_days);
      setWinDelay(data.config.win_delay_days);
      
      const wheelSegments = MobilePromoWheelConfig.createWheelSegments(data.items);
      setSegments(wheelSegments);
    } catch (error) {
      console.error('Erreur chargement roue:', error);
      setIsActive(false);
    }
  };

  // Fonction de rotation de la roue (utilise MobilePromoWheelConfig)
  const spinWheel = async () => {
    const validation = MobilePromoWheelConfig.validateCanPlay(
      isAuthenticated, 
      userActivePromotions, 
      canPlay, 
      daysUntilNextPlay
    );

    if (!validation.canPlay) {
      if (validation.message === 'login_required') {
        setShowLoginModal(true);
        return;
      }
      if (validation.message === 'active_promotions') {
        alert(MobilePromoWheelConfig.MESSAGES.activePromotions);
        return;
      }
      if (validation.message?.startsWith('wait_')) {
        const waitTime = validation.message.replace('wait_', '');
        alert(`Vous devez attendre encore ${waitTime}.`);
        return;
      }
    }

    if (isSpinning || segments.length === 0) return;

    setIsSpinning(true);
    setShowResult(false);
    
    try {
      const result = await MobilePromoWheelConfig.performSpin(segments);
      const finalRotation = MobilePromoWheelConfig.calculateWheelRotation(segments, result.winning_item.id);
      
      setRotation(finalRotation);
      
      setTimeout(async () => {
        setIsSpinning(false);
        const savedPromotion = await MobilePromoWheelConfig.savePromoCode(result, isAuthenticated);
        setWonPromotion(savedPromotion);
        setShowResult(true);
        setCanPlay(false);
        loadWheelData();
      }, MobilePromoWheelConfig.SPIN_CONFIG.duration);
      
    } catch (error: any) {
      setIsSpinning(false);
      alert(error.message || 'Erreur');
    }
  };
  
  // Modales et animations (identiques au desktop mais adaptées mobile)

  if (!isActive) return null;

  return (
    <div className="py-4 px-4 bg-gradient-to-b from-blue-50/30 to-white">
      {/* Titre mobile - Agrandi */}
      <div className="text-center mb-4">
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center space-x-1 mb-1"
        >
          <span className="text-xl">✨</span>
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
            Roue de Promotions
          </h2>
        </motion.div>
        <p className="text-sm text-gray-600">
          🎁 Gagnez jusqu'à <span className="text-blue-600 font-bold">50% de réduction</span>!
        </p>
      </div>

      {/* Message promotions actives - Compact */}
      {isAuthenticated && userActivePromotions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3 max-w-xs mx-auto"
        >
          <div className="text-center mb-2">
            <span className="text-lg">🎯</span>
            <h3 className="font-bold text-gray-800 text-sm">Promotions actives!</h3>
          </div>
          {userActivePromotions.slice(0, 1).map((promo) => (
            <div key={promo.id} className="bg-white rounded-md p-2">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 text-xs truncate">{promo.name}</p>
                  <p className="text-xs text-gray-600">{promo.discount_display}</p>
                </div>
                <button
                  onClick={() => window.location.href = '/devisrequest'}
                  className="bg-green-500 text-white px-2 py-1 rounded text-xs font-medium hover:bg-green-600 transition-colors ml-2"
                >
                  Réclamez
                </button>
              </div>
            </div>
          ))}
          {userActivePromotions.length > 1 && (
            <p className="text-center text-xs text-gray-500 mt-1">
              +{userActivePromotions.length - 1} autre{userActivePromotions.length - 1 > 1 ? 's' : ''}
            </p>
          )}
        </motion.div>
      )}

      {/* Section principale mobile - Ultra compacte */}
      <div className="flex justify-center">
        <div className="flex flex-col items-center space-y-3">
          {/* Iris Assistant Mobile - Agrandi */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/90 rounded-xl shadow-md border border-blue-100 p-3 max-w-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white shadow-md">
                  <img 
                    src={avatarUrl} 
                    alt="Iris" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/irisavatar.jpg';
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm">Iris</h3>
                  <p className="text-xs text-blue-600">Assistante IA</p>
                </div>
              </div>
              <button
                onClick={() => setShowRules(!showRules)}
                className="px-2 py-1 bg-blue-50 rounded-md text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors"
              >
                {showRules ? '▲' : '📋 Règles'}
              </button>
            </div>

            <div className="mt-2">
              <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded p-1.5 border-l-2 border-blue-500">
                {!isAuthenticated ? (
                  <p className="text-xs text-gray-700">👋 Connectez-vous pour jouer</p>
                ) : !canPlay ? (
                  <p className="text-xs text-gray-700">⏰ Revenez dans {MobilePromoWheelConfig.formatDelay(daysUntilNextPlay)}</p>
                ) : (
                  <p className="text-xs text-gray-700">🎉 Cliquez pour tourner la roue!</p>
                )}
              </div>

              {/* Règles mobile ultra compactes */}
              <AnimatePresence>
                {showRules && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden mt-1"
                  >
                    <div className="bg-gray-50 rounded p-1.5">
                      <p className="text-xs text-gray-600">
                        • Connectez-vous • Cliquez "TOURNER" • Code auto • 1 fois / {MobilePromoWheelConfig.formatDelay(replayDelay)}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Roue Mobile - Structure comme desktop */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`flex justify-center items-center ${
              isAuthenticated && userActivePromotions.length > 0 
                ? 'opacity-40 grayscale pointer-events-none' 
                : ''
            }`}
          >
            <div className="relative">
              {/* Flèche pointeur - Mini */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
                <div className="w-0 h-0 
                  border-l-[6px] border-l-transparent 
                  border-r-[6px] border-r-transparent 
                  border-t-[10px] border-t-yellow-500 
                  drop-shadow-sm">
                </div>
              </div>

              <div className="relative p-2 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg">
                <div className="relative w-40 h-40 rounded-full bg-white p-1 shadow-inner">
                  <div 
                    ref={wheelRef}
                    onClick={spinWheel}
                    className={`w-full h-full rounded-full relative shadow-sm ${
                      !isSpinning && isAuthenticated && canPlay && userActivePromotions.length === 0 ? 'cursor-pointer hover:scale-[1.02] transition-transform' : 
                      isSpinning ? 'cursor-wait' : 'cursor-not-allowed opacity-60'
                    }`}
                    style={{ 
                      transform: `rotate(${rotation}deg)`,
                      transition: isSpinning ? MobilePromoWheelConfig.SPIN_CONFIG.transition : 'transform 0.2s ease'
                    }}
                  >
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      {segments.map((segment, index) => {
                        const angle = 360 / segments.length;
                        const startAngle = index * angle - 90;
                        const endAngle = startAngle + angle;
                        
                        const x1 = 50 + 50 * Math.cos((startAngle * Math.PI) / 180);
                        const y1 = 50 + 50 * Math.sin((startAngle * Math.PI) / 180);
                        const x2 = 50 + 50 * Math.cos((endAngle * Math.PI) / 180);
                        const y2 = 50 + 50 * Math.sin((endAngle * Math.PI) / 180);
                        
                        const largeArcFlag = angle > 180 ? 1 : 0;
                        
                        const textAngle = startAngle + angle / 2;
                        const textRadius = 30;
                        const textX = 50 + textRadius * Math.cos((textAngle * Math.PI) / 180);
                        const textY = 50 + textRadius * Math.sin((textAngle * Math.PI) / 180);
                        
                        const { textColor, strokeColor } = MobilePromoWheelConfig.getTextColor(segment.color);
                        
                        return (
                          <g key={segment.id}>
                            <path
                              d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                              fill={segment.color}
                              stroke="#fff"
                              strokeWidth="0.8"
                            />
                            <text
                              x={textX}
                              y={textY}
                              fontSize="2.5"
                              fontWeight="900"
                              fill={textColor}
                              stroke={strokeColor}
                              strokeWidth="0.4"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                              style={{ 
                                fontFamily: 'Arial Black, Arial, sans-serif',
                                paintOrder: 'stroke fill'
                              }}
                            >
                              {segment.label}
                            </text>
                          </g>
                        );
                      })}
                    </svg>

                    {/* Centre de la roue - Agrandi */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-white to-gray-100 shadow-lg border-3 border-yellow-400 flex items-center justify-center">
                        {isSpinning ? (
                          <svg className="animate-spin h-4 w-4 text-blue-600" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <span className="text-base">🎯</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton mobile - Agrandi */}
              <div className="text-center mt-3">
                <button
                  onClick={spinWheel}
                  disabled={isSpinning || !isAuthenticated || !canPlay || (isAuthenticated && userActivePromotions.length > 0)}
                  className={`px-6 py-2.5 rounded-full font-bold text-white text-sm shadow-lg transition-all ${
                    isSpinning || !isAuthenticated || !canPlay || (isAuthenticated && userActivePromotions.length > 0)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 hover:shadow-xl active:scale-95'
                  }`}
                >
                  {isSpinning ? '⏳ Tourne...' : 
                   !isAuthenticated ? '🔒 Connexion' : 
                   (isAuthenticated && userActivePromotions.length > 0) ? '🎯 Promos actives' :
                   !canPlay ? `⏰ ${MobilePromoWheelConfig.formatDelay(daysUntilNextPlay)}` : 
                   '🎰 TOURNER'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modal Résultat Mobile */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowResult(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-yellow-200/50 to-blue-200/50 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                {wonPromotion ? (
                  <div className="text-center">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="text-6xl mb-4"
                    >
                      🎉
                    </motion.div>
                    <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-blue-900 bg-clip-text text-transparent">
                      {MobilePromoWheelConfig.MESSAGES.congratulations}
                    </h3>
                    
                    <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-4 mb-4 border-2 border-blue-100">
                      <p className="text-3xl font-bold text-blue-600 mb-2">{wonPromotion.discount_display}</p>
                      <p className="font-semibold text-gray-900 mb-2">{wonPromotion.name}</p>
                      <p className="text-sm text-gray-600 mb-3">{wonPromotion.description}</p>
                      
                      {/* Affichage spécial pour les promotions "Achetez X obtenez Y" */}
                      {wonPromotion.discount_type === 'buy_x_get_y' && wonPromotion.buy_service_id && wonPromotion.get_service_id && (
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-3 mb-3 border border-green-200">
                          <p className="text-xs font-semibold text-gray-700 mb-2">🎯 Détails de l'offre :</p>
                          <div className="space-y-1 text-xs">
                            <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-center">
                              Achetez : {MobilePromoWheelConfig.getServiceName(wonPromotion.buy_service_id, services)}
                            </div>
                            <div className="text-center text-gray-400">↓</div>
                            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-center">
                              Obtenez : {MobilePromoWheelConfig.getServiceName(wonPromotion.get_service_id, services)}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-white rounded-xl p-3 border-2 border-dashed border-blue-300 shadow-inner">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Votre code promo</p>
                        <p className="text-xl font-mono font-bold text-blue-600 tracking-wider">{wonPromotion.code}</p>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-500 mb-4">
                      ✨ {wonPromotion.end_date ? 
                        `Valable jusqu'au ${new Date(wonPromotion.end_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}` : 
                        'Promotion sans date d\'expiration'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                      className="text-6xl mb-4"
                    >
                      😊
                    </motion.div>
                    <h3 className="text-2xl font-bold mb-4 text-gray-800">{MobilePromoWheelConfig.MESSAGES.notThisTime}</h3>
                    <p className="text-gray-600 mb-4">{MobilePromoWheelConfig.MESSAGES.retryIn} {MobilePromoWheelConfig.formatDelay(replayDelay)}</p>
                  </div>
                )}
                
                <button
                  onClick={() => {
                    setShowResult(false);
                    setWonPromotion(null);
                  }}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-900 shadow-lg hover:shadow-xl transition-all"
                >
                  {MobilePromoWheelConfig.BUTTONS.close}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Connexion Mobile */}
      <AnimatePresence>
        {showLoginModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLoginModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6 relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-200/50 to-yellow-200/50 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="text-center">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="text-6xl mb-4"
                  >
                    🔒
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-blue-900 bg-clip-text text-transparent">
                    Connexion requise
                  </h3>
                  
                  <div className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-2xl p-4 mb-4 border-2 border-blue-100">
                    <p className="text-gray-700 mb-3">
                      {MobilePromoWheelConfig.MESSAGES.loginRequired}
                    </p>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p className="flex items-center justify-center gap-2">
                        <span>✨</span>
                        <span>Gagnez jusqu'à 50% de réduction</span>
                      </p>
                      <p className="flex items-center justify-center gap-2">
                        <span>🎯</span>
                        <span>Codes promo exclusifs</span>
                      </p>
                      <p className="flex items-center justify-center gap-2">
                        <span>🎁</span>
                        <span>Offres spéciales et cadeaux</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setShowLoginModal(false);
                        navigate('/login');
                      }}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-900 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                      {MobilePromoWheelConfig.BUTTONS.connect}
                    </button>
                    
                    <button
                      onClick={() => setShowLoginModal(false)}
                      className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
                    >
                      {MobilePromoWheelConfig.BUTTONS.maybeLater}
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    Pas encore de compte ?{' '}
                    <button
                      onClick={() => {
                        setShowLoginModal(false);
                        navigate('/register');
                      }}
                      className="text-blue-600 font-semibold hover:text-blue-700 underline"
                    >
                      {MobilePromoWheelConfig.BUTTONS.register}
                    </button>
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobilePromoWheel;
